import { useCallback, useState } from "react";
import { useAuth } from "@/context/auth/AuthContext";
import { uploadImage } from "@/api/image";
import { Attachment as UploadAttachmentPayload } from "@/lib/types";
import {
  createFicheFromImport,
  rollbackImportedFiche,
} from "@/api/batch-import";
import { extractUploadErrorMessage } from "@/components/images/batchUploadErrors";
import { queryClient } from "@/lib/queryClient";
import {
  BATCH_IMPORT_MAIN_POSITION,
  BatchImportType,
  fileNameToTitle,
} from "./batchImportConfig";

export type BatchItemStatus =
  | "pending"
  | "creating"
  | "uploading"
  | "success"
  | "error"
  | "skipped";

export interface BatchImportItem {
  uid: string;
  file: File;
  title: string;
  status: BatchItemStatus;
  /** Raw error code/message (i18n key candidate). */
  error?: string;
  ficheId?: string;
  registrationNumber?: string;
  thumbnailUrl?: string;
}

/** Statuts "terminés" d'un item (pour le calcul de progression). */
export const FINISHED_STATUSES: BatchItemStatus[] = [
  "success",
  "error",
  "skipped",
];

/** Erreur convertisseur qui doit stopper tout le batch (quota atteint). */
const STOP_BATCH_ERROR = "quotaExceded";

export function useBatchImport(type: BatchImportType) {
  const { instanceData } = useAuth();
  const [items, setItems] = useState<BatchImportItem[]>([]);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);

  const reset = useCallback(() => {
    setItems([]);
    setRunning(false);
    setDone(false);
  }, []);

  const patch = useCallback((uid: string, partial: Partial<BatchImportItem>) => {
    setItems((prev) =>
      prev.map((it) => (it.uid === uid ? { ...it, ...partial } : it)),
    );
  }, []);

  const run = useCallback(
    async (files: File[]) => {
      if (!instanceData || running || files.length === 0) return;

      const list: BatchImportItem[] = files.map((file, i) => ({
        uid: `${i}-${file.name}`,
        file,
        title: fileNameToTitle(file.name),
        status: "pending" as const,
      }));
      setItems(list);
      setRunning(true);
      setDone(false);

      let stopped = false;

      for (const item of list) {
        if (stopped) {
          patch(item.uid, { status: "skipped" });
          continue;
        }

        // 1) Créer la fiche (titre = nom fichier, n° temp incrémental).
        patch(item.uid, { status: "creating", error: undefined });
        let ficheId: string;
        let registrationNumber: string;
        try {
          const fiche = await createFicheFromImport(type, item.title);
          ficheId = fiche.id;
          registrationNumber = fiche.registrationNumber;
          patch(item.uid, { ficheId, registrationNumber });
        } catch (error) {
          patch(item.uid, {
            status: "error",
            error: extractUploadErrorMessage(error),
          });
          continue;
        }

        // 2) Uploader l'image en position principale via le convertisseur.
        //    L'image de face est publiée par défaut (demande client).
        patch(item.uid, { status: "uploading" });
        try {
          const attachment = await uploadImage(
            item.file,
            {
              name: item.title,
              position: BATCH_IMPORT_MAIN_POSITION[type],
              docId: ficheId,
              order: 1,
              published: true,
              applyWatermark: true,
              isHd: false,
            } as unknown as UploadAttachmentPayload,
            instanceData.s3Bucket,
            instanceData.id,
            undefined, // API_URL par défaut -> attachment/create/unique (face)
            item.title, // displayName
          );
          const thumbnailUrl =
            attachment?.mediumPublicUrl ??
            attachment?.smallPublicUrl ??
            attachment?.publicUrl;
          patch(item.uid, { status: "success", thumbnailUrl });
        } catch (error) {
          // 3) Rollback : la conversion a échoué -> pas de fiche vide orpheline.
          const code = extractUploadErrorMessage(error);
          try {
            await rollbackImportedFiche(type, ficheId);
          } catch {
            // rollback best-effort
          }
          patch(item.uid, {
            status: "error",
            error: code,
            ficheId: undefined,
            registrationNumber: undefined,
          });
          if (code === STOP_BATCH_ERROR) stopped = true;
        }
      }

      // Rafraîchir les listes/compteurs impactés par les créations.
      await queryClient.invalidateQueries();

      setRunning(false);
      setDone(true);
    },
    [instanceData, running, type, patch],
  );

  return { items, running, done, run, reset };
}

export type UseBatchImportReturn = ReturnType<typeof useBatchImport>;
